// src/components/Timesheets/TimesheetDashboard.tsx

import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../../../hooks/useAuth";
import * as XLSX from "xlsx";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../../../firebase/firebase";
import { parseISO, format } from "date-fns";
import { getTimesheetPermissions } from "../../../utils/permissions";

import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  useColorMode,
} from "@chakra-ui/react";
import {
  FaEye,
  FaSearch,
  FaSort,
  FaFileDownload,
} from "react-icons/fa";
import { User } from "../userPerformace/UserPerformance";
import UserDetailsPage from "./UserDetailsPage";
import { IconWrapper } from "../../../components/IconWrapper";
import { getThemeClasses } from "../../../utils/themeUtils";
import CustomDropdown from '../../../components/CustomDropdown';
import { formatDateDDMMYYYY } from "../../../utils/dateUtils";
import { showToast } from "../../../utils/toastUtils";

interface TimesheetDashboardProps {
  users: User[];
  organizationId: string;
  color: string;
  bg: string;
  selectedUser: User | null;
  setSelectedUser: (user: User | null) => void;
  currentUser?: any;
}

// Component for a single user table row
interface UserTableRowProps {
  user: User;
  color: string;
  onViewClick: (user: User) => void;
  isDark: boolean;
  theme: any;
  rowRef?: React.Ref<HTMLTableRowElement>;
}

const toTitle = (s: string) => s.replace(/\b\w/g, c => c.toUpperCase());


const UserTableRow: React.FC<UserTableRowProps> = ({
  user,
  color,
  onViewClick,
  isDark,
  theme,
  rowRef,
}) => (
  <tr ref={rowRef} key={user.id} className={`${isDark ? 'hover:bg-[#2A3441]' : 'hover:bg-[#F2F4F7]'} cursor-pointer border-b ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}>
    <td className="px-2 py-2 whitespace-nowrap text-sm">
      <div className="flex items-center">
        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[#B59143] border border-[#B59143] text-xs font-bold mr-2 `}>
          {user.firstName && user.firstName.length > 0 ? user.firstName.charAt(0).toUpperCase() : '?'}
        </div>
        <span className={isDark ? 'text-white' : 'text-gray-900'}>
          {user.firstName} {user.lastName || ''}
        </span>
      </div>
    </td>
    <td className={`px-2 py-2 whitespace-nowrap text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${isDark ? 'bg-transparent border border-[#B59143] text-[#FBD282]' : 'bg-[white] text-[#B59143] border border-[#E8C16C]'}`}>
        {toTitle(user.role)}
      </span>
    </td>
    <td className="px-2 py-2 whitespace-nowrap text-sm text-center">
      <button
        className={`text-xs px-3 py-1 rounded transition ${isDark
          ? 'border border-[#B59143] text-[#FBD282] hover:bg-[#2A3441] focus:ring-2 focus:ring-[#B59143] focus:ring-opacity-50'
          : 'border border-[#B59143] text-[#B59143] hover:bg-[#FFF7E6] focus:ring-2 focus:ring-[#B59143] focus:ring-opacity-30'
          }`}
        onClick={() => onViewClick(user)}
      >
        View Details
      </button>
    </td>
  </tr>
);

const TimesheetDashboard: React.FC<TimesheetDashboardProps> = ({
  users,
  organizationId,
  color,
  bg,
  selectedUser,
  setSelectedUser,
  currentUser,
}) => {
  const { user: authUser } = useAuth();
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);

  // Determine if the current viewer has privileged access (Admin, Manager, Managing Director)
  // Use currentUser prop as fallback if not found in users array
  const currentViewer = users.find((u) => u.id === authUser?.uid) || currentUser;
  const tsPerms = useMemo(() => getTimesheetPermissions(currentViewer), [currentViewer]);

  // Users visible to the current viewer
  // Org admin always sees all users without permission checks
  const isOrgAdmin = currentUser?.isOrgAdmin || currentUser?.permissions?.toLowerCase() === "admin" || currentUser?.role?.toLowerCase() === "admin";
  const visibleUsers = (tsPerms.canViewAll || isOrgAdmin)
    ? users
    : currentViewer
      ? [currentViewer]
      : [];

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPosition, setSelectedPosition] = useState(""); // State for position filter

  // Display & Export filters - START WITH EMPTY to avoid expensive fetch on mount
  const initialNow = new Date();
  const [displayMonth, setDisplayMonth] = useState<string>("");  // Empty = "All Months"
  const [displayYear, setDisplayYear] = useState<string>("");    // Empty = "All Years"

  // Separate state for users filtered by timesheet period
  const [usersWithTimesheets, setUsersWithTimesheets] = useState<Set<string>>(new Set());
  const [loadingTimesheets, setLoadingTimesheets] = useState(false);

  // Fetch users with timesheets in selected period
  useEffect(() => {
    const fetchUsersWithTimesheets = async () => {
      // If no filters selected, show all users
      if (!displayMonth && !displayYear) {
        setUsersWithTimesheets(new Set(visibleUsers.map(u => u.id)));
        return;
      }

      setLoadingTimesheets(true);
      const usersSet = new Set<string>();

      try {
        await Promise.all(
          visibleUsers.map(async (user) => {
            // Weekly architecture: TimeSheetsData/{userId}/weeks/{weekMonday}
            const tsColRef = collection(db, "organization", organizationId, "TimeSheetsData", user.id, "weeks");
            const snap = await getDocs(tsColRef);

            let allEntries: any[] = [];
            if (!snap.empty) {
              snap.docs.forEach(wkDoc => {
                const wkData = wkDoc.data();
                const entries = wkData.entries || [];
                allEntries.push(...entries);
              });
            } else {
              // Fallback to legacy
              const oldRef = doc(db, "organization", organizationId, "timesheets", user.id);
              const oldSnap = await getDoc(oldRef);
              if (oldSnap.exists()) {
                allEntries = oldSnap.data().entries || [];
              }
            }

            if (allEntries.length === 0) return;

            const hasMatchingEntry = allEntries.some((e) => {
              if (!e?.date) return false;
              try {
                const d = parseISO(e.date);
                const m = String(d.getMonth() + 1).padStart(2, "0");
                const y = String(d.getFullYear());
                const monthOk = !displayMonth || m === displayMonth;
                const yearOk = !displayYear || y === displayYear;
                return monthOk && yearOk;
              } catch {
                return false;
              }
            });

            if (hasMatchingEntry) {
              usersSet.add(user.id);
            }
          })
        );
      } catch (error) {
        console.error("Error fetching timesheets for filtering:", error);
      }

      setUsersWithTimesheets(usersSet);
      setLoadingTimesheets(false);
    };

    fetchUsersWithTimesheets();
  }, [displayMonth, displayYear, organizationId, visibleUsers]);

  const filteredUsers = visibleUsers.filter(
    (user) =>
      `${user.firstName} ${user.lastName} ${user.role}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) &&
      (selectedPosition === "" ||
        user.role.toLowerCase().trim() === selectedPosition) &&
      (!displayMonth && !displayYear ? true : usersWithTimesheets.has(user.id))
  );



  if (selectedUser) {
    return (
      <UserDetailsPage
        user={selectedUser}
        organizationId={organizationId}
        onBack={() => setSelectedUser(null)}
        color={color}
        bg={bg}
      />
    );
  }

  //OverAll Export

  const handleExportAll = async (monthFilter: string = displayMonth, yearFilter: string = displayYear) => {
    try {
      // 1️⃣ Fetch raw timesheet entries for every visible user
      const userSheets = await Promise.all(
        visibleUsers.map(async (u) => {
          // Weekly architecture: TimeSheetsData/{userId}/weeks/{weekMonday}
          const tsColRef = collection(db, "organization", organizationId, "TimeSheetsData", u.id, "weeks");
          const snap = await getDocs(tsColRef);

          let entries: any[] = [];
          if (!snap.empty) {
            snap.docs.forEach(wkDoc => {
              const wkData = wkDoc.data();
              const weekEntries = wkData.entries || [];
              entries.push(...weekEntries);
            });
          } else {
            // Fallback to legacy
            const oldRef = doc(db, "organization", organizationId, "timesheets", u.id);
            const oldSnap = await getDoc(oldRef);
            if (oldSnap.exists()) {
              entries = oldSnap.data().entries || [];
            }
          }
          return {
            name: `${u.firstName} ${u.lastName}`,
            entries,
          };
        })
      );

      // 2️⃣ Build an AOA (array‑of‑arrays) for Excel
      const aoa: any[][] = [];

      // 2a. Global header—only once
      aoa.push([
        "User",
        "Date",
        "Hours",
        "JobCode",
        "Project",
        "Status",
        "Task",
      ]);

      // 2b. For each user, filter by selected month/year if provided and emit rows
      userSheets.forEach(({ name, entries }) => {
        // Apply month/year filter if set
        const filteredEntries = entries.filter((e) => {
          if (!e?.date) return false;
          try {
            const d = parseISO(e.date);
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const y = String(d.getFullYear());
            const monthOk = !monthFilter || m === monthFilter;
            const yearOk = !yearFilter || y === yearFilter;
            return monthOk && yearOk;
          } catch {
            return false;
          }
        });

        if (filteredEntries.length === 0) return; // skip users with no rows after filtering

        filteredEntries.forEach((e, idx) => {
          aoa.push([
            idx === 0 ? name : "", // name only on first detail row
            formatDateDDMMYYYY(e.date),
            e.hours,
            e.jobCode,
            e.project,
            e.status,
            e.taskDescription,
          ]);
        });
      });

      // 3️⃣ Convert AOA → worksheet & workbook
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "AllTimesheets");

      // 4️⃣ Trigger download
      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const monthLabel = monthFilter
        ? format(new Date(0, parseInt(monthFilter) - 1), "MMMM")
        : "AllMonths";
      const yearLabel = yearFilter || "AllYears";
      a.download = `Timesheets_${monthLabel}_${yearLabel}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // 🎉 Feedback
      showToast({
        title: "Export Successful",
        description: "All timesheets exported with consolidated header format.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error("ExportAll error:", err);
      showToast({
        title: "Export Failed",
        description:
          "There was an issue exporting timesheets. Please try again or contact support.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Get unique roles for the filter dropdown
  const uniqueRoles = Array.from(
    new Set(visibleUsers.map((user) => user.role.trim().toLowerCase()))
  ); // Trim whitespace and convert to lowercase

  return (
    <div className="flex flex-col overflow-hidden max-w-full">
      {/* Main Heading Section - Dynamic Theme */}
      <div className={`flex-shrink-0 flex justify-between items-center p-4 ${theme.headerGradient} rounded-md mb-4 border ${theme.border}`}>
        {isDark && (
          <>
            <span className={theme.headerOverlayGlow} />
            <span className={theme.headerOverlayTopLine} />
            <span className={theme.headerOverlayBottomGlow} />
            <span className={theme.headerOverlaySweep} />
          </>
        )}
        <div className="flex flex-col space-y-1">
          <span className={`text-2xl font-bold ${theme.text}`}>
            Timesheet Analysis
          </span>
          <span className={`text-xs ${theme.textMuted}`}>
            View and analyze user timesheet data with comprehensive insights.
          </span>
        </div>
      </div>

      {/* Search Bar, Filters, and Count Section */}
      <div className={`w-full rounded-md border ${theme.border} ${theme.card}`}>
        <div className={`flex justify-between items-center px-4 py-3 border-b ${theme.border} rounded-t-md flex-wrap flex-shrink-0`}>
          <div className="flex items-center space-x-4 flex-1">
            {!tsPerms.isViewer && (
              <div className="w-40 flex-shrink-0">
                <CustomDropdown
                  value={selectedPosition}
                  onChange={setSelectedPosition}
                  isDark={isDark}
                  options={[
                    { value: "", label: "All Positions" },
                    ...uniqueRoles.map((role) => ({ value: role, label: toTitle(role) }))
                  ]}
                  className={`w-full px-3 py-1.5 text-sm rounded-full border focus:outline-none focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143] ${theme.searchInput}`}
                />
              </div>
            )}
            <div className={`relative flex items-center max-w-sm w-full rounded-full ${theme.cardMuted} overflow-hidden`}>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <IconWrapper icon={FaSearch} className={`h-4 w-4 ${theme.textMuted}`} size={16} />
              </div>
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`${theme.searchInput} block w-full pl-10 pr-3 py-1.5 border rounded-full leading-5 focus:outline-none sm:text-sm`}
              />
            </div>
            {!tsPerms.isNormalUser && (
              <>
                <CustomDropdown
                  value={displayMonth}
                  onChange={(value) => setDisplayMonth(value)}
                  isDark={isDark}
                  options={[
                    { value: '', label: 'All Months' },
                    ...Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1).padStart(2, '0'), label: format(new Date(0, i), 'MMMM') }))
                  ]}
                  placeholder="Select Month"
                  className={`w-36 px-3 py-1.5 text-sm rounded-full border focus:outline-none focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143] ${theme.searchInput}`}
                />
                <CustomDropdown
                  value={displayYear}
                  onChange={(value) => setDisplayYear(value)}
                  isDark={isDark}
                  options={[
                    { value: '', label: 'All Years' },
                    ...Array.from({ length: 6 }, (_, i) => {
                      const y = new Date().getFullYear() - i;
                      return { value: String(y), label: String(y) };
                    })
                  ]}
                  placeholder="Select Year"
                  className={`w-28 px-3 py-1.5 text-sm rounded-full border focus:outline-none focus:ring-2 focus:ring-[#B59143] focus:border-[#B59143] ${theme.searchInput}`}
                />
                <button
                  onClick={() => handleExportAll(displayMonth, displayYear)}
                  className={`flex items-center px-3 py-1.5 ${theme.primaryButton} rounded-full text-sm focus:outline-none flex-shrink-0`}
                  title="Export filtered users with current month/year filter"
                  disabled={loadingTimesheets}
                >
                  <IconWrapper icon={FaFileDownload} size={16} className="mr-1" />
                  {loadingTimesheets ? 'Loading...' : 'Export'}
                </button>
              </>
            )}
          </div>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold mt-4 md:mt-0 flex-shrink-0 ml-2 ${isDark ? 'text-[#C3C9D3] border border-[#4A4D54]' : 'text-gray-600 border border-gray-300'}`}>
            {filteredUsers.length} members
          </span>
        </div>

        <div className="p-4">
          {/* Table Container */}
          <div className={`flex flex-col w-full rounded-md overflow-hidden ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33] shadow-[0_6px_24px_rgba(0,0,0,0.35)]' : 'bg-white border border-gray-200 shadow-sm'}`}>
            {/* Scrollable Table Area */}
            <div
              className="overflow-y-auto"
              style={{ maxHeight: "68vh" }}
            >
              <table className="min-w-full">
                <thead className={`sticky top-0 z-10 ${isDark ? 'bg-[#14161B]' : 'bg-gray-50'}`}>
                  <tr>
                    <th className={`px-2 py-2 text-left text-xs font-semibold uppercase tracking-wider border-b ${isDark ? 'text-white border-[#2E2F33]' : 'text-gray-600 border-gray-200'}`}>
                      Name
                    </th>
                    <th className={`px-2 py-2 text-left text-xs font-semibold uppercase tracking-wider border-b ${isDark ? 'text-white border-[#2E2F33]' : 'text-gray-600 border-gray-200'}`}>
                      <span className="inline-flex items-center">
                        <span>Position</span>
                        <IconWrapper icon={FaSort} size={12} className={`ml-1 ${theme.textMuted}`} />
                      </span>
                    </th>
                    <th className={`px-2 py-2 text-center text-xs font-semibold uppercase tracking-wider border-b ${isDark ? 'text-white border-[#2E2F33]' : 'text-gray-600 border-gray-200'}`}>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? 'bg-[#1A1C1F]' : 'bg-white'}`}>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((user, index) => (
                      <UserTableRow
                        key={user.id}
                        user={user}
                        color={color}
                        onViewClick={setSelectedUser}
                        isDark={isDark}
                        theme={theme}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className={`text-center py-8 text-lg font-medium ${isDark ? 'text-[#C3C9D3]' : 'text-gray-500'}`}>
                        No users found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimesheetDashboard;
